import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback
} from "react";

import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  Divider,
  Typography,
  Collapse,
} from "@mui/material";

import {
  Inventory as InventoryIcon,
  Dashboard as DashboardIcon,
  Autorenew as AutorenewIcon,
  AccountCircle as AccountCircleIcon,
  Warehouse as WarehouseIcon,
  Checklist as ChecklistIcon,
  Assignment as AssignmentIcon,
  AllInbox as AllInboxIcon,
  ExpandLess,
  ExpandMore,
  LocationOn as LocationOnIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import StoreIcon from "@mui/icons-material/Store";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AuthContext } from "../../auth/authContext";
import routes from "config/routes";
import useResponsive from "shared/hooks/useResponsive";

import { DRAWER_WIDTH } from 'shared/constants/constants';

/* -------------------------------------------------
  Route → model permission mapping (BACKEND)
------------------------------------------------- */
const MODEL_MAP = {
  [routes.ORDERS]: "orders",
  [routes.LOCATIONS]: "locations",
  [routes.STOCK]: "declinationlocation",
  [routes.INVENTORY]: "inventories",
  [routes.REPLENISHMENT]: "replenishments",
  [routes.MISSIONS]: "pickings",
  [routes.SHIPMENT]: "shipments",
  [routes.PACKINGS]: "packings",
  [routes.CONTACTS]: "contacts",
  [routes.USERS]: "accounts_user",
};

/* -------------------------------------------------
   Menu brut
------------------------------------------------- */
const RAW_MENU = [
  {
    textKey: "sidebar.dashboard",
    icon: <DashboardIcon />,
    path: routes.DASHBOARD,
  },
  {
    textKey: "sidebar.locations",
    icon: <LocationOnIcon />,
    path: routes.LOCATIONS,
  },
  {
    textKey: "sidebar.operations",
    icon: <InventoryIcon />,
    children: [
      { textKey: "sidebar.orders", icon: <InventoryIcon />, path: routes.ORDERS },
      { textKey: "sidebar.stock", icon: <WarehouseIcon />, path: routes.STOCK },
      { textKey: "sidebar.inventory", icon: <ChecklistIcon />, path: routes.INVENTORY },
      { textKey: "sidebar.replenishment", icon: <AutorenewIcon />, path: routes.REPLENISHMENT },
      { textKey: "sidebar.missions", icon: <AssignmentIcon />, path: routes.MISSIONS },
    ],
  },
  {
    textKey: "sidebar.shipment",
    icon: <LocalShippingIcon />,
    children: [
      { textKey: "sidebar.missions", icon: <LocalShippingIcon />, path: routes.SHIPMENT },
      { textKey: "sidebar.packings", icon: <AllInboxIcon />, path: routes.PACKINGS },
    ],
  },
  {
    textKey: "sidebar.userAndRoleManagement",
    icon: <GroupIcon />,
    children: [
      {
        textKey: "sidebar.users",
        icon: <AccountCircleIcon />,
        path: routes.USERS,
        adminOnly: true,
      },
      {
        textKey: "sidebar.roles",
        icon: <AutorenewIcon />,
        path: routes.PERMISSIONS_ADMIN,
        adminOnly: true,
      },
    ],
  },
  {
    textKey: "sidebar.shop",
    icon: <StoreIcon />,
    path: routes.SHOP,
  },
  {
    textKey: "sidebar.contacts",
    icon: <AccountCircleIcon />,
    path: routes.CONTACTS,
  },
];

const Sidebar = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();
  const { isMobile, isDesktop } = useResponsive();

  const [openGroups, setOpenGroups] = useState({});

  /* -------------------------------------------------
     Permission helper (READ)
  ------------------------------------------------- */
  const hasReadPermission = useCallback(
    (path) => {
      const model = MODEL_MAP[path];
      if (!model) return true;

      const permissions = user?.permissions || {};
      return permissions.read?.includes(model);
    },
    [user]
  );

  /* -------------------------------------------------
     Role helper
  ------------------------------------------------- */
  const hasAdminRole = useCallback(() => {
    const roles = user?.roles || [];
    return roles.some((r) =>
      ["Admin", "Manager", "SuperAdmin"].includes(r)
    );
  }, [user]);

  /* -------------------------------------------------
     Auto-open active group
  ------------------------------------------------- */
  useEffect(() => {
    const opened = {};
    RAW_MENU.forEach((item) => {
      if (item.children?.some((c) => c.path === location.pathname)) {
        opened[item.textKey] = true;
      }
    });
    setOpenGroups(opened);
  }, [location.pathname]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* -------------------------------------------------
     Menu (memoized)
  ------------------------------------------------- */
  const menu = useMemo(() => {
    if (!user) return [];

    return RAW_MENU.map((item) => {
      if (!item.children) {
        return hasReadPermission(item.path) ? item : null;
      }

      const children = item.children.filter((child) => {
        if (child.adminOnly && !hasAdminRole()) return false;
        return hasReadPermission(child.path);
      });

      return children.length ? { ...item, children } : null;
    }).filter(Boolean);
  }, [user, hasReadPermission, hasAdminRole]);

  if (loading || !user) return null;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      open={open}
      onClose={onClose}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          borderRight: isDesktop ? "1px solid #e0e0e0" : "none",
        },
      }}
    >
      <Toolbar>
        <Typography variant="h6" fontWeight={600}>
          {t("sidebar.title")}
        </Typography>
      </Toolbar>

      <Divider />

      <List>
        {menu.map((item) => {
          const isActive = location.pathname === item.path;

          if (item.children) {
            const openGroup = openGroups[item.textKey];

            return (
              <React.Fragment key={item.textKey}>
                <ListItemButton onClick={() => toggleGroup(item.textKey)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={t(item.textKey)} />
                  {openGroup ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={openGroup} timeout="auto">
                  <List disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.textKey}
                        component={Link}
                        to={child.path}
                        selected={location.pathname === child.path}
                        onClick={isMobile ? onClose : undefined}
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText primary={t(child.textKey)} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItemButton
              key={item.textKey}
              component={Link}
              to={item.path}
              selected={isActive}
              onClick={isMobile ? onClose : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={t(item.textKey)} />
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
